import React, { useState, useContext } from 'react';
import { Form, Button, Alert, Spinner, Card, Row, Col, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../context/AuthContext';
import { ThemeContext } from '../../context/ThemeContext';
import axiosInstance from '../../api/axios';
import { translateRole, translateStatus } from '../../i18n';
import UserAvatar from '../../components/common/UserAvatar';

function Profile() {
  const { t } = useTranslation();
  const { user, token, login, logout } = useContext(AuthContext);
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Profile Info States
  const [nombreCompleto, setNombreCompleto] = useState(user?.nombre_completo || '');
  const [email, setEmail] = useState(user?.email || '');
  const [telefono, setTelefono] = useState(user?.telefono || '');
  
  // Password States [BA-01 HU37]
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Logout Modal State [FE-01 HU37]
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loadingLogout, setLoadingLogout] = useState(false);

  // UI States
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [infoSuccess, setInfoSuccess] = useState('');
  const [infoError, setInfoError] = useState('');
  const [passSuccess, setPassSuccess] = useState('');
  const [passError, setPassError] = useState('');

  const handleUpdateInfo = async (e) => {
    e.preventDefault();
    setInfoSuccess('');
    setInfoError('');
    setLoadingInfo(true);

    try {
      const response = await axiosInstance.put(`/admin/users/${user.id}`, {
        nombre_completo: nombreCompleto,
        email: email,
        rol: user.rol,
        telefono: telefono,
        status: user.status || 'Activo'
      });

      if (response.data.success) {
        const updatedUser = response.data.data;
        login(updatedUser, token);
        setInfoSuccess('Información de perfil actualizada exitosamente.');
      } else {
        setInfoError(response.data.message || 'Error al actualizar la información.');
      }
    } catch (err) {
      setInfoError(err.response?.data?.message || 'Error al conectar con el servidor.');
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPassSuccess('');
    setPassError('');

    if (!currentPassword) {
      setPassError('Debe ingresar su contraseña actual.');
      return;
    }

    if (newPassword.length < 6) {
      setPassError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPassError('Las contraseñas no coinciden.');
      return;
    }

    setLoadingPassword(true);

    try {
      const response = await axiosInstance.put('/auth/password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword
      });

      if (response.data.success) {
        setPassSuccess('Tu contraseña se ha actualizado correctamente. Redirigiendo al inicio de sesión...');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');

        // Automatically invalidate session and redirect to login
        setTimeout(() => {
          logout();
          navigate('/user-pages/login');
        }, 2000);
      } else {
        setPassError(response.data.message || 'Error al actualizar la contraseña.');
      }
    } catch (err) {
      setPassError(err.response?.data?.message || 'La contraseña actual no es correcta o hubo un error.');
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleLogoutConfirm = async () => {
    setLoadingLogout(true);
    try {
      await axiosInstance.post('/auth/logout');
    } catch (err) {
      console.warn("Backend logout request failed, clearing local session:", err);
    } finally {
      setLoadingLogout(false);
      setShowLogoutModal(false);
      logout();
      navigate('/user-pages/login');
    }
  };

  const handleConfirmLogout = handleLogoutConfirm;

  return (
    <>
      <div className="page-header">
        <h3 className="page-title" style={{ fontWeight: 'bold' }}>
          {t('profile_title', 'Mi Perfil de Usuario')}
        </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="!#" onClick={event => event.preventDefault()}>{t('menu_dashboard', 'Dashboard')}</a></li>
            <li className="breadcrumb-item active" aria-current="page">{t('profile', 'Perfil')}</li>
          </ol>
        </nav>
      </div>

      <Row>
        {/* Left Side Profile Card */}
        <Col lg={4} className="mb-4">
          <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ height: '100px', backgroundColor: '#00024f', borderBottom: '3px solid #183eeb' }}></div>
            <Card.Body className="text-center" style={{ marginTop: '-60px' }}>
              <div className="d-inline-block mb-3">
                <UserAvatar 
                  user={user} 
                  size="xl" 
                  className="shadow-sm" 
                  style={{ border: '4px solid #ffffff' }}
                />
              </div>

              <h4 style={{ fontWeight: 'bold' }} className="mb-1">
                {user ? user.nombre_completo : 'Usuario Rutex'}
              </h4>
              <p className="text-muted text-uppercase small fw-bold mb-3">
                {user ? translateRole(user.rol, t) : t('role_customer', 'Cliente')}
              </p>

              <hr className="my-4" />

              <div className="text-start px-2">
                <div className="mb-3">
                  <span className="text-muted d-block small text-uppercase fw-bold mb-1">{t('email', 'Correo Electrónico')}</span>
                  <span style={{ fontSize: '14px' }}>{user ? user.email : 'usuario@rutex.com'}</span>
                </div>
                
                <div className="mb-3">
                  <span className="text-muted d-block small text-uppercase fw-bold mb-1">{t('field_phone', 'Teléfono')}</span>
                  <span style={{ fontSize: '14px' }}>{user?.telefono || t('not_registered', 'No registrado')}</span>
                </div>

                <div className="mb-3">
                  <span className="text-muted d-block small text-uppercase fw-bold mb-1">{t('account_status', 'Estado de Cuenta')}</span>
                  <div className="mt-1">
                    <span className="badge bg-success-light text-success fw-bold px-3 py-2 small" style={{ backgroundColor: '#e2f9ec', borderRadius: '6px' }}>
                      {user?.status ? translateStatus(user.status, t).toUpperCase() : t('status_active', 'ACTIVO')}
                    </span>
                  </div>
                </div>
              </div>

              <hr className="my-4" />

              {/* Logout Button [FE-01 HU37] */}
              <div className="d-grid gap-2">
                <Button 
                  variant="outline-danger" 
                  onClick={() => setShowLogoutModal(true)}
                  className="fw-bold py-2"
                  style={{ borderRadius: '8px' }}
                >
                  <i className="mdi mdi-logout me-2"></i>{t('secure_logout', 'Cerrar Sesión Segura')}
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Theme Preferences Card [FE-01 HU37 Escenario 2] */}
          <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
            <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
              <h5 style={{ fontWeight: 'bold' }} className="mb-0">
                <i className="mdi mdi-theme-light-dark text-primary me-2"></i>{t('appearance_preferences', 'Preferencias de Apariencia')}
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="fw-bold mb-1">{t('dark_light_mode', 'Modo Oscuro / Claro')}</h6>
                  <p className="text-muted small mb-0">{t('dark_light_description', 'Alterna la apariencia visual de toda la interfaz en tiempo real')}</p>
                </div>
                <Form.Check 
                  type="switch"
                  id="custom-switch-theme"
                  checked={isDark}
                  onChange={toggleTheme}
                  style={{ transform: 'scale(1.3)', cursor: 'pointer' }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Side Editing Forms */}
        <Col lg={8}>
          {/* Card 1: Profile Info */}
          <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: '12px' }}>
            <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
              <h5 style={{ fontWeight: 'bold' }} className="mb-0">
                <i className="mdi mdi-account-card-details text-primary me-2"></i>{t('edit_profile_info', 'Editar Información del Perfil')}
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              {infoSuccess && <Alert variant="success">{infoSuccess}</Alert>}
              {infoError && <Alert variant="danger">{infoError}</Alert>}

              <Form onSubmit={handleUpdateInfo}>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="text-muted small fw-bold">{t('field_full_name', 'Nombre Completo')}</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={nombreCompleto} 
                        onChange={(e) => setNombreCompleto(e.target.value)} 
                        required 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="text-muted small fw-bold">{t('th_email', 'Correo Electrónico')}</Form.Label>
                      <Form.Control 
                        type="email" 
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                        required 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-4">
                    <Form.Group>
                      <Form.Label className="text-muted small fw-bold">{t('field_phone', 'Teléfono')}</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={telefono} 
                        onChange={(e) => setTelefono(e.target.value)} 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={loadingInfo}
                    className="px-4 py-2"
                    style={{ borderRadius: '8px', fontWeight: 'bold' }}
                  >
                    {loadingInfo ? <><Spinner size="sm" animation="border" className="me-2" /> {t('btn_saving', 'Guardando...')}</> : t('btn_save_changes', 'Guardar Cambios')}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Card 2: Change Password [BA-01 & FE-01 HU37] */}
          <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
            <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
              <h5 style={{ fontWeight: 'bold' }} className="mb-0">
                <i className="mdi mdi-lock-reset text-primary me-2"></i>{t('change_secure_password', 'Cambiar Contraseña Segura')}
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              {passSuccess && <Alert variant="success">{passSuccess}</Alert>}
              {passError && <Alert variant="danger">{passError}</Alert>}

              <Form onSubmit={handleUpdatePassword}>
                <Row>
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label className="text-muted small fw-bold">{t('current_password', 'Contraseña Actual *')}</Form.Label>
                      <Form.Control 
                        type="password" 
                        placeholder={t('enter_current_password', 'Ingrese su contraseña actual')}
                        value={currentPassword} 
                        onChange={(e) => setCurrentPassword(e.target.value)} 
                        required 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="text-muted small fw-bold">{t('new_password', 'Nueva Contraseña *')}</Form.Label>
                      <Form.Control 
                        type="password" 
                        placeholder={t('min_6_chars', 'Mínimo 6 caracteres')}
                        value={newPassword} 
                        onChange={(e) => setNewPassword(e.target.value)} 
                        required 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-4">
                    <Form.Group>
                      <Form.Label className="text-muted small fw-bold">{t('confirm_new_password', 'Confirmar Nueva Contraseña *')}</Form.Label>
                      <Form.Control 
                        type="password" 
                        placeholder={t('repeat_new_password', 'Repita la nueva contraseña')}
                        value={confirmPassword} 
                        onChange={(e) => setConfirmPassword(e.target.value)} 
                        required 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button 
                    type="submit" 
                    variant="dark" 
                    disabled={loadingPassword}
                    className="px-4 py-2"
                    style={{ borderRadius: '8px', fontWeight: 'bold' }}
                  >
                    {loadingPassword ? <><Spinner size="sm" animation="border" className="me-2" /> {t('btn_updating', 'Actualizando...')}</> : t('btn_update_password', 'Actualizar Contraseña')}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirmation Modal for Logout [FE-01 HU37] */}
      <Modal show={showLogoutModal} onHide={() => setShowLogoutModal(false)} centered>
        <Modal.Header closeButton className="border-0 pt-4 px-4">
          <Modal.Title className="fw-bold text-danger">
            <i className="mdi mdi-alert-circle-outline me-2"></i>{t('confirm_logout_title', 'Confirmar Cierre de Sesión')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-2">
          <p className="text-secondary mb-0">
            {t('confirm_logout_msg', '¿Estás seguro de que deseas cerrar sesión? Tu token de acceso activo será revocado por seguridad en el servidor.')}
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-4">
          <Button variant="light" onClick={() => setShowLogoutModal(false)} className="px-3">
            {t('btn_cancel', 'Cancelar')}
          </Button>
          <Button variant="danger" onClick={handleLogoutConfirm} disabled={loadingLogout} className="px-4 fw-bold">
            {loadingLogout ? <><Spinner size="sm" animation="border" className="me-2" /> {t('loading', 'Saliendo...')}</> : t('btn_yes_logout', 'Sí, Cerrar Sesión')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Profile;